// Follow-up List Manager Modal (WP-04).
import { useState, useEffect, useCallback, useId, useRef } from 'react';
import { useI18n } from '../../lib/i18n';
import { cn } from '../../lib/cn';
import { useDialogA11y } from '../../lib/useDialogA11y';
import {
  listFollowups,
  completeFollowup,
  deleteFollowup,
  exportFollowups,
  type FollowupDoc,
} from '../../lib/followups';
import { ClockIcon, CloseIcon, DownloadIcon, CheckIcon, TrashIcon } from '../icons';
import { useToast } from '../ui/Toast';
import { useConfirm } from '../ui/ConfirmDialog';

interface FollowupListModalProps {
  onClose: () => void;
  onSelectConversation?: (conversationId: string) => void;
}

type StatusFilter = 'all' | 'open' | 'done' | 'cancelled';

export default function FollowupListModal({
  onClose,
  onSelectConversation,
}: FollowupListModalProps) {
  const { t } = useI18n();
  const toast = useToast();
  const confirm = useConfirm();
  const [statusFilter, setStatusFilter] = useState<StatusFilter>('all');
  const [items, setItems] = useState<FollowupDoc[]>([]);
  const [loading, setLoading] = useState<boolean>(true);
  const [exporting, setExporting] = useState<boolean>(false);
  const [error, setError] = useState<string | null>(null);
  const panelRef = useRef<HTMLDivElement>(null);
  const titleId = useId();
  useDialogA11y(true, panelRef, onClose);

  const fetchItems = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const res = await listFollowups({
        status: statusFilter === 'all' ? undefined : statusFilter,
        limit: 100,
      });
      if (res.ok && Array.isArray(res.items)) {
        setItems(res.items);
      }
    } catch (err) {
      setError((err as Error).message || 'Failed to fetch follow-ups');
    } finally {
      setLoading(false);
    }
  }, [statusFilter]);

  useEffect(() => {
    void fetchItems();
  }, [fetchItems]);

  const handleComplete = async (id: string, newStatus: 'done' | 'cancelled') => {
    try {
      await completeFollowup({ id, status: newStatus });
      setItems((prev) =>
        prev.map((it) => (it.id === id ? { ...it, status: newStatus } : it)),
      );
    } catch (err) {
      toast.error((err as Error).message || t('followup.updateFailed'));
    }
  };

  const handleDelete = async (id: string) => {
    if (!(await confirm({ title: t('followup.deleteConfirm'), body: t('common.cannotUndo'), danger: true }))) return;
    try {
      await deleteFollowup({ id });
      setItems((prev) => prev.filter((it) => it.id !== id));
    } catch (err) {
      toast.error((err as Error).message || t('followup.deleteFailed'));
    }
  };

  const handleExport = async () => {
    setExporting(true);
    try {
      const res = await exportFollowups({
        status: statusFilter === 'all' ? undefined : statusFilter,
      });
      if (res.ok && res.url) {
        window.open(res.url, '_blank');
      }
    } catch (err) {
      toast.error((err as Error).message || t('followup.exportFailed'));
    } finally {
      setExporting(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-slate-900/40 p-4 backdrop-blur-sm">
      <div
        ref={panelRef}
        role="dialog"
        aria-modal="true"
        aria-labelledby={titleId}
        className="flex h-[85vh] max-h-[640px] w-full max-w-3xl flex-col overflow-hidden rounded-2xl border border-line bg-white shadow-2xl dark:border-slate-800 dark:bg-slate-900"
      >
        {/* Header */}
        <div className="flex items-center justify-between border-b border-line px-5 py-4 dark:border-slate-800">
          <div className="flex items-center gap-2.5">
            <div className="grid h-8 w-8 place-items-center rounded-lg bg-amber-50 text-amber-600 dark:bg-amber-950/40 dark:text-amber-400">
              <ClockIcon size={18} />
            </div>
            <div>
              <h3 id={titleId} className="text-sm font-bold text-ink dark:text-slate-100">
                {t('followup.listTitle') || 'Follow-up Reminders'}
              </h3>
              <p className="text-xs text-ink-3 dark:text-slate-400">
                {t('followup.listSubtitle') || 'Manage and track conversation follow-up commitments'}
              </p>
            </div>
          </div>
          <div className="flex items-center gap-2">
            <button
              type="button"
              onClick={handleExport}
              disabled={exporting}
              className="flex items-center gap-1.5 rounded-xl border border-line bg-surface px-3 py-1.5 text-xs font-bold text-slate-700 hover:bg-slate-100 disabled:opacity-50 dark:border-slate-800 dark:bg-slate-800 dark:text-slate-300 dark:hover:bg-slate-750"
            >
              <DownloadIcon size={14} />
              <span>{exporting ? t('common.exporting') || 'Exporting…' : t('followup.exportCsv') || 'Download CSV'}</span>
            </button>
            <button
              type="button"
              onClick={onClose}
              aria-label={t('common.close') || 'Close'}
              className="grid h-8 w-8 place-items-center rounded-lg text-slate-400 hover:bg-slate-100 dark:hover:bg-slate-800"
            >
              <CloseIcon size={16} />
            </button>
          </div>
        </div>

        {/* Filter Tabs */}
        <div className="flex border-b border-line px-5 pt-2 dark:border-slate-800">
          {(['all', 'open', 'done', 'cancelled'] as StatusFilter[]).map((tab) => (
            <button
              key={tab}
              type="button"
              onClick={() => setStatusFilter(tab)}
              className={cn(
                'border-b-2 px-4 py-2.5 text-xs font-bold capitalize transition-colors',
                statusFilter === tab
                  ? 'border-brand text-brand'
                  : 'border-transparent text-slate-500 hover:text-slate-800 dark:text-slate-400 dark:hover:text-slate-200',
              )}
            >
              {t(`followup.status.${tab}`) || tab}
            </button>
          ))}
        </div>

        {/* Table Content */}
        <div className="min-h-0 flex-1 overflow-y-auto p-4">
          {error && (
            <div className="mb-3 rounded-lg bg-rose-50 p-3 text-xs font-semibold text-rose-600 dark:bg-rose-950/40 dark:text-rose-400">
              {error}
            </div>
          )}

          {loading ? (
            <div className="grid h-48 place-items-center text-xs text-ink-3 dark:text-slate-500">
              {t('common.loading') || 'Loading follow-ups…'}
            </div>
          ) : items.length === 0 ? (
            <div className="grid h-48 place-items-center text-xs text-ink-3 dark:text-slate-500">
              {t('followup.empty') || 'No follow-ups found'}
            </div>
          ) : (
            <div className="space-y-2">
              {items.map((item) => {
                const isOverdue = item.status === 'open' && item.dueAt < Date.now();
                const dueStr = new Date(item.dueAt).toLocaleString([], {
                  month: 'short',
                  day: 'numeric',
                  hour: '2-digit',
                  minute: '2-digit',
                });
                return (
                  <div
                    key={item.id}
                    className="flex flex-col sm:flex-row sm:items-center justify-between gap-3 rounded-xl border border-line bg-surface p-3.5 transition-colors hover:border-slate-300 dark:border-slate-800 dark:bg-slate-850 dark:hover:border-slate-700"
                  >
                    <div className="min-w-0 flex-1">
                      <div className="flex items-center gap-2">
                        <span
                          className={cn(
                            'inline-flex items-center gap-1 rounded-full px-2 py-0.5 text-[10px] font-bold uppercase tracking-wider',
                            item.status === 'done'
                              ? 'bg-emerald-100 text-emerald-700 dark:bg-emerald-950/40 dark:text-emerald-400'
                              : item.status === 'cancelled'
                              ? 'bg-slate-100 text-slate-600 dark:bg-slate-800 dark:text-slate-400'
                              : isOverdue
                              ? 'bg-rose-100 text-rose-700 dark:bg-rose-950/40 dark:text-rose-400'
                              : 'bg-amber-100 text-amber-700 dark:bg-amber-950/40 dark:text-amber-400',
                          )}
                        >
                          {item.status}
                          {isOverdue && ' · OVERDUE'}
                        </span>
                        <span className="text-xs font-bold text-ink dark:text-slate-200">
                          ⏱ {dueStr}
                        </span>
                      </div>

                      <div className="mt-1 text-xs font-medium text-ink dark:text-slate-100">
                        {item.reason}
                      </div>

                      {item.note && (
                        <div className="mt-0.5 text-xs text-ink-3 dark:text-slate-400">
                          {item.note}
                        </div>
                      )}
                    </div>

                    <div className="flex items-center gap-1.5 shrink-0">
                      {onSelectConversation && (
                        <button
                          type="button"
                          onClick={() => {
                            onSelectConversation(item.conversationId);
                            onClose();
                          }}
                          className="rounded-lg border border-line px-2.5 py-1.5 text-xs font-bold text-slate-700 hover:bg-slate-100 dark:border-slate-800 dark:text-slate-300 dark:hover:bg-slate-800"
                        >
                          {t('followup.openChat') || 'Open Chat'}
                        </button>
                      )}

                      {item.status === 'open' && (
                        <button
                          type="button"
                          onClick={() => void handleComplete(item.id, 'done')}
                          title={t('followup.markDone') || 'Mark Done'}
                          className="flex items-center gap-1 rounded-lg bg-emerald-600 px-2.5 py-1.5 text-xs font-bold text-white shadow-sm hover:bg-emerald-700"
                        >
                          <CheckIcon size={14} />
                          <span>{t('followup.done') || 'Done'}</span>
                        </button>
                      )}

                      <button
                        type="button"
                        onClick={() => void handleDelete(item.id)}
                        aria-label={t('common.delete') || 'Delete'}
                        className="grid h-8 w-8 place-items-center rounded-lg text-slate-400 hover:bg-rose-50 hover:text-rose-600 dark:hover:bg-rose-950/40 dark:hover:text-rose-400"
                      >
                        <TrashIcon size={15} />
                      </button>
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
